// CD Atelier — approved Image 1 direction
// Purpose: prototype the vision reliably. Works as a static page if GSAP/CDNs fail;
// enhances with GSAP/SplitType only when available.
(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasGSAP = !!(window.gsap && window.ScrollTrigger);
  const hasSplit = !!window.SplitType;
  if (hasGSAP) gsap.registerPlugin(ScrollTrigger);

  function removeCurtainAndStart() {
    const curtain = document.getElementById('curtain');
    if (curtain) curtain.remove();
    document.body.classList.remove('is-loading');
    startSequence();
  }

  // Starts fetching the hero photo as soon as the curtain ("Atelier") is on screen, so it
  // loads behind it. The hero stays black until this resolves (see .hero--ready in atelier.css).
  // The race caps the wait at 8s so a failed or slow image can't leave the hero black.
  let heroImageLoaded = Promise.resolve();
  function loadHeroImage() {
    const hero = document.querySelector('.hero');
    if (!hero) return;
    const m = /url\(["']?([^"')]+)["']?\)/.exec(getComputedStyle(hero).getPropertyValue('--atelier-hero1-url'));
    if (!m) return;
    const img = new Image();
    img.src = m[1];
    heroImageLoaded = Promise.race([
      img.decode().catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, 8000))
    ]);
  }

  function raiseCurtain() {
    loadHeroImage();
    const curtain = document.getElementById('curtain');
    if (!curtain || reducedMotion || !hasGSAP) {
      removeCurtainAndStart();
      return;
    }
    const failsafe = setTimeout(removeCurtainAndStart, 2500);
    const tl = gsap.timeline({ onComplete: () => { clearTimeout(failsafe); removeCurtainAndStart(); } });
    tl.to('.curtain-tag', { opacity: 1, duration: 0.25 })
      .to('.curtain-line span', { scaleX: 1, duration: 0.45, ease: 'expo.out' }, '<')
      .to('.curtain-word', { opacity: 1, duration: 0.55, ease: 'expo.out' }, '-=0.2')
      .to('.curtain-tag, .curtain-word, .curtain-line', { opacity: 0, duration: 0.25 })
      .to(curtain, { yPercent: -100, duration: 0.8, ease: 'expo.inOut' });
  }

  function splitChars(el) {
    if (!hasSplit || !el) return [];
    try { return new SplitType(el, { types: 'chars' }).chars || []; } catch (e) { return []; }
  }

  function splitWords(el) {
    if (!hasSplit || !el) return [];
    try { return new SplitType(el, { types: 'words' }).words || []; } catch (e) { return []; }
  }

  function reveal(target, vars, opts) {
    if (!hasGSAP || reducedMotion) return;
    const trigger = opts && opts.trigger ? opts.trigger : target;
    ScrollTrigger.create({
      trigger,
      start: (opts && opts.start) || 'top 78%',
      once: true,
      onEnter: () => gsap.from(target, { duration: 0.9, ease: 'expo.out', ...vars })
    });
  }

  function setupHeroTorch() {
    const hero = document.querySelector('.hero');
    if (!hero) return;

    function setPos(x, y, smooth) {
      const r = hero.getBoundingClientRect();
      const px = ((x - r.left) / r.width) * 100;
      const py = ((y - r.top) / r.height) * 100;
      const clamp = (n) => Math.max(0, Math.min(100, n));
      const mx = clamp(px) + '%';
      const my = clamp(py) + '%';
      if (hasGSAP && smooth && !reducedMotion) {
        gsap.to(hero, { '--mx': mx, '--my': my, duration: 0.45, ease: 'power3.out' });
      } else {
        hero.style.setProperty('--mx', mx);
        hero.style.setProperty('--my', my);
      }
    }

    if (!window.matchMedia('(pointer: coarse)').matches && !reducedMotion) {
      hero.addEventListener('mousemove', (e) => setPos(e.clientX, e.clientY, true));
      hero.addEventListener('mouseleave', () => {
        if (hasGSAP) gsap.to(hero, { '--mx': '62%', '--my': '58%', duration: 1.1, ease: 'power3.out' });
      });
    }

    // The curtain has finished by now. Start the lit effect once the photo is also loaded.
    heroImageLoaded.then(() => requestAnimationFrame(() => hero.classList.add('hero--ready')));

    // Touch screens have no cursor to carry the torch, so it wanders on its own.
    // The dark spot-mask itself is plain CSS (atelier.css), so it shows before this runs.
    if (hasGSAP && !reducedMotion && window.matchMedia('(pointer: coarse), (max-width: 880px)').matches) {
      const rand = (min, max) => min + Math.random() * (max - min);
      const drift = () => gsap.to(hero, {
        '--mx': rand(22, 78) + '%',
        '--my': rand(32, 72) + '%',
        duration: rand(2.4, 3.6),
        ease: 'sine.inOut',
        onComplete: drift
      });
      drift();
    }

    if (!hasGSAP || reducedMotion) return;
    const wordLine = document.querySelector('.hero-word-line');
    const chars = splitChars(wordLine);
    if (chars.length) {
      gsap.to(chars, {
        yPercent: -60,
        opacity: 0,
        stagger: { each: 0.018, from: 'center' },
        ease: 'none',
        scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: 0.6 }
      });
    }
    gsap.to('.hero-reveal-bg', {
      scale: 1.14,
      yPercent: -7,
      ease: 'none',
      scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true }
    });
    gsap.to('.hero-orbit-one', {
      x: -80, y: 60, rotate: 9, ease: 'none',
      scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true }
    });
    gsap.to('.hero-orbit-two', {
      x: 80, y: -50, rotate: -7, ease: 'none',
      scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true }
    });
    gsap.to('.hero-sub, .hero-cta, .hero-meta-bottom-right', {
      opacity: 0, y: -20, ease: 'none',
      scrollTrigger: { trigger: '.hero', start: 'top top', end: '45% top', scrub: 0.5 }
    });
  }

  function setupManifesto() {
    document.querySelectorAll('.manifesto-line p').forEach((p) => {
      const words = splitWords(p);
      if (words.length) reveal(words, { yPercent: 110, opacity: 0, stagger: 0.045 }, { trigger: p.closest('.manifesto-line') });
    });
  }

  function setupProcessBanner() {
    reveal('.process-banner-img', { opacity: 0, scale: 1.04 }, { trigger: '.process-banner', start: 'top 75%' });
    reveal('.process-card', { opacity: 0, y: 30, stagger: .08 }, { trigger: '.exploded-stories', start: 'top 78%' });
  }

  function setupGallery() {
    const gallery = document.querySelector('.gallery');
    if (!gallery) return;

    reveal('.gallery-opener-title, .gallery-opener-copy, .gallery-opener-cue', { opacity: 0, y: 34, stagger: .09 }, { trigger: '.gallery-opener', start: 'top 68%' });

    document.querySelectorAll('.gallery-room').forEach((room, index) => {
      const scene = room.querySelector('.gallery-scene');
      const object = room.querySelector('.gallery-object');
      const objectImage = room.querySelector('.gallery-object img');
      const plinth = room.querySelector('.gallery-plinth');
      const labelBits = room.querySelectorAll('.gallery-label > *');
      const meta = room.querySelector('.gallery-room-meta');

      if (!reducedMotion && !window.matchMedia('(pointer: coarse)').matches) {
        room.addEventListener('mousemove', (event) => {
          const rect = room.getBoundingClientRect();
          const raw = ((event.clientX - rect.left) / rect.width) * 100;
          const x = Math.max(30, Math.min(70, raw));
          const tilt = (x - 50) * 0.065;
          if (hasGSAP) {
            gsap.to(room, { '--lamp-x': x + '%', '--lamp-tilt': tilt + 'deg', duration: .48, ease: 'power3.out', overwrite: true });
          } else {
            room.style.setProperty('--lamp-x', x + '%');
            room.style.setProperty('--lamp-tilt', tilt + 'deg');
          }
        });
        room.addEventListener('mouseleave', () => {
          if (hasGSAP) gsap.to(room, { '--lamp-x': '50%', '--lamp-tilt': '0deg', duration: .8, ease: 'power3.out' });
          else {
            room.style.setProperty('--lamp-x', '50%');
            room.style.setProperty('--lamp-tilt', '0deg');
          }
        });
      }

      if (!hasGSAP || reducedMotion) return;

      ScrollTrigger.create({
        trigger: room,
        start: 'top 62%',
        once: true,
        onEnter: () => {
          room.classList.add('is-lit');
          gsap.from(object, { opacity: 0, scale: .91, y: 48, duration: 1.25, ease: 'expo.out' });
          gsap.from(plinth, { opacity: 0, y: 34, duration: 1.05, ease: 'expo.out', delay: .05 });
          gsap.from(meta, { opacity: 0, y: 16, duration: .72, ease: 'expo.out', delay: .18 });
          gsap.from(labelBits, { opacity: 0, x: room.classList.contains('gallery-room-reverse') ? -26 : 26, stagger: .065, duration: .86, ease: 'expo.out', delay: .2 });
          if (objectImage) {
            gsap.to(objectImage, { y: -11, duration: 3.2 + (index * .12), repeat: -1, yoyo: true, ease: 'sine.inOut' });
          }
        }
      });

      if (scene) {
        gsap.fromTo(scene, { yPercent: 4 }, {
          yPercent: -4,
          ease: 'none',
          scrollTrigger: { trigger: room, start: 'top bottom', end: 'bottom top', scrub: .8 }
        });
      }
    });
  }

  function setupProducts() {
    if (!document.querySelector('.atelier-products')) return;
    reveal('.atelier-products-head, .atelier-product-card', { opacity: 0, y: 28, stagger: .08 }, { trigger: '.atelier-products', start: 'top 78%' });
  }

  function setupChrome() {
    const chrome = document.querySelector('.chrome');
    if (!chrome) return;
    let lastY = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      if (y < 80 || y < lastY - 8) chrome.classList.remove('is-hidden');
      else if (y > lastY + 8) chrome.classList.add('is-hidden');
      lastY = y;
    }, { passive: true });
  }

  // Gallery "Buy Now" CTAs smooth-scroll to the product row (#atelier-acquire).
  // Works WITH Lenis when an instance is exposed as window.lenis (delegates to its
  // scrollTo so we never fight its scroll loop); otherwise falls back to native smooth
  // scroll. Links without a hash target (mailto fallback when the row is hidden) are
  // left untouched and behave as normal links.
  function setupAcquireScroll() {
    const links = document.querySelectorAll('a[data-acquire-scroll]');
    if (!links.length) return;
    links.forEach((link) => {
      link.addEventListener('click', (e) => {
        const hash = link.getAttribute('href') || '';
        if (hash.charAt(0) !== '#' || hash.length < 2) return;
        const target = document.querySelector(hash);
        if (!target) return; // no row on the page → don't hijack the click
        e.preventDefault();
        const lenis = window.lenis;
        if (lenis && typeof lenis.scrollTo === 'function') {
          lenis.scrollTo(target, { offset: 0, duration: reducedMotion ? 0 : 1.1 });
        } else {
          target.scrollIntoView(reducedMotion ? true : { behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  function startSequence() {
    setupHeroTorch();
    setupManifesto();
    setupProcessBanner();
    setupGallery();
    setupProducts();
    setupAcquireScroll();
    setupChrome();
    if (hasGSAP) {
      // Mobile browsers fire `resize` whenever the address bar shows/hides,
      // which only changes innerHeight. Tell ScrollTrigger to ignore those so
      // scrubbed sections don't jump or leave dead scroll space; do a real
      // refresh on orientation change (and after fonts load) instead.
      ScrollTrigger.config({ ignoreMobileResize: true });
      setTimeout(() => ScrollTrigger.refresh(), 250);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => ScrollTrigger.refresh());
      let orientTimer;
      window.addEventListener('orientationchange', () => {
        clearTimeout(orientTimer);
        orientTimer = setTimeout(() => ScrollTrigger.refresh(), 300);
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', raiseCurtain);
  else raiseCurtain();
})();
